/* A looping, touch-friendly carousel that survives Shopify section reloads. */
if (!customElements.get('tr-review-carousel')) {
  customElements.define('tr-review-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.track = this.querySelector('[data-review-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-review-slide]:not([data-review-clone])'));
      this.count = this.slides.length;
      this.controls = this.querySelector('[data-review-controls]');
      this.toggle = this.querySelector('[data-review-toggle]');
      this.dots = Array.from(this.querySelectorAll('[data-review-dot]'));
      this.active = 0;
      this.events = new AbortController();
      const listen = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal: this.events.signal });
      if (this.count < 2) return;

      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.userPaused = !this.autoplay || this.motion.matches;
      this.hovered = false;
      this.inView = !('IntersectionObserver' in window);
      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);

      // Keep clones out of the accessibility tree and Shopify's block selector.
      const copies = () => {
        const fragment = document.createDocumentFragment();
        this.slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.dataset.reviewClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          [clone, ...clone.querySelectorAll('*')].forEach((element) => {
            element.removeAttribute('id');
            element.removeAttribute('data-shopify-editor-block');
          });
          fragment.append(clone);
        });
        return fragment;
      };
      this.track.prepend(copies());
      this.track.append(copies());
      if (this.controls) this.controls.hidden = !this.controls.querySelector('button');

      this.measure = () => {
        const width = this.slides[0].getBoundingClientRect().width;
        if (!width) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = width + gap;
        if (Math.abs(step - (this.step || 0)) < 0.5) return;
        this.step = step;
        this.base = this.count * this.step;
        this.jump(this.base + this.active * this.step);
        this.schedule();
      };
      this.measure();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.track);
      } else {
        listen(window, 'resize', this.measure);
      }
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.15 });
        this.visibilityObserver.observe(this);
      }

      listen(this.track, 'scroll', () => {
        this.clearTimer();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 140);
      }, { passive: true });
      listen(this.track, 'pointerdown', () => this.pause());
      listen(this.track, 'wheel', () => this.pause(), { passive: true });
      listen(this, 'pointerenter', (event) => {
        if (event.pointerType !== 'touch') { this.hovered = true; this.schedule(); }
      });
      listen(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      // Focus and manual navigation pause rotation until Play is requested.
      listen(this, 'focusin', (event) => { if (event.target !== this.toggle) this.pause(); });
      listen(this.toggle, 'focusin', () => { this.focusSuspended = true; this.clearTimer(); });
      listen(this.toggle, 'focusout', () => {
        if (this.focusSuspended) this.pause();
        this.focusSuspended = false;
      });
      listen(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.focusSuspended = false;
        this.updateControls();
        this.schedule();
      });
      listen(this.querySelector('[data-review-prev]'), 'click', () => this.go(this.active - 1, true));
      listen(this.querySelector('[data-review-next]'), 'click', () => this.go(this.active + 1, true));
      this.dots.forEach((dot) => listen(dot, 'click', () => this.go(Number(dot.dataset.reviewDot), true)));
      listen(this.track, 'keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.active + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.motion, 'change', () => { if (this.motion.matches) this.pause(); });
      listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.reviewId === event.detail?.blockId);
        if (index !== -1) this.go(index, true);
      });
      this.updateControls();
      this.schedule();
    }

    normalized(index) { return ((index % this.count) + this.count) % this.count; }

    jump(left) {
      this.track.scrollTo({ left, behavior: 'auto' });
    }

    go(index, manual = false) {
      if (!this.step || !this.isConnected) return;
      if (manual) this.pause();
      this.clearTimer();
      this.track.scrollTo({ left: this.base + index * this.step, behavior: this.motion.matches ? 'auto' : 'smooth' });
      this.active = this.normalized(index);
      this.updateControls();
      if (manual) {
        const status = this.querySelector('[data-review-status]');
        if (status) status.textContent = `Review ${this.active + 1} of ${this.count}`;
      }
      // Also settle when a browser does not fire scroll (e.g. an active dot).
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 750);
    }

    settle() {
      if (!this.step || !this.isConnected) return;
      const index = Math.round((this.track.scrollLeft - this.base) / this.step);
      this.active = this.normalized(index);
      if (index < 0 || index >= this.count) this.jump(this.base + this.active * this.step);
      this.updateControls();
      this.schedule();
    }

    pause() {
      this.userPaused = true;
      this.clearTimer();
      this.updateControls();
    }

    clearTimer() { clearTimeout(this.timer); this.timer = null; }

    schedule() {
      this.clearTimer();
      if (!this.autoplay || this.userPaused || this.hovered || this.focusSuspended || !this.inView || document.hidden || !this.step || !this.isConnected) return;
      this.timer = setTimeout(() => this.go(this.active + 1), this.interval);
    }

    updateControls() {
      this.dots.forEach((dot, index) => {
        if (index === this.active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (!this.toggle) return;
      const label = this.toggle.querySelector('[data-review-toggle-label]');
      if (label) label.textContent = this.userPaused ? 'Play' : 'Pause';
      this.toggle.setAttribute('aria-label', this.userPaused ? 'Start automatic review sliding' : 'Pause automatic review sliding');
      this.toggle.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.userPaused);
      this.toggle.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.userPaused);
    }

    disconnectedCallback() {
      this.clearTimer();
      clearTimeout(this.settleTimer);
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.track?.querySelectorAll('[data-review-clone]').forEach((clone) => clone.remove());
      if (this.controls) this.controls.hidden = true;
      this.events = null;
      this.step = null;
      this.focusSuspended = false;
    }
  });
}
